import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Button,
  Breadcrumbs,
  Container,
  LinearProgress,
  IconButton,
  Grid,
  Paper,
  Stack,
  Typography,
  Snackbar,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Divider,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';

import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import { alpha, useTheme, Fade, Grow } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';


import SpeakerImage from '../../media/Lamp Poses-01-1.png';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import { useApi } from '../../hooks/useApi';
import { AuthContext } from '../../AuthContext';

function Index() {
  const jApi = useApi();
  const navigate = useNavigate();
  const { getUser } = useContext(AuthContext);
  const theme = useTheme();

  const [tests, setTests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isJobPosting, setIsJobPosting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);

  // Gradient header style
  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
    position: 'relative',
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`,
      opacity: 0.5,
    },
  };

  const [tenantName, setTenantName] = useState('');
  const [executedAt, setExecutedAt] = useState('');
  const [total, setTotal] = useState(0);
  const [passed, setPassed] = useState(0);
  const [failed, setFailed] = useState(0);

  // Blade / flyout state
  const [selectedRow, setSelectedRow] = useState(null);
  const bladeOpen = !!selectedRow;

  const passedPercent = total > 0 ? (passed / total) * 100 : 0;
  const failedPercent = total > 0 ? (failed / total) * 100 : 0;

  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRows = useMemo(() => {
    if (!tests || tests.length === 0) return [];

    return [...tests].sort((a, b) => {
      if (orderBy === 'id') {
        const idA = a?.Id ?? '';
        const idB = b?.Id ?? '';
        return order === 'asc' ? idA.localeCompare(idB) : idB.localeCompare(idA);
      }
      return 0;
    });
  }, [tests, order, orderBy]);

  const formattedLocalTime = useMemo(() => {
    if (!executedAt) return '—';
    try {
      const date = new Date(executedAt);
      return date.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return 'Invalid date';
    }
  }, [executedAt]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const selected_account = sessionStorage.getItem('account');

      const response = await jApi.request(
        'GET',
        'auth/validate_appreg_assessment/account/' + selected_account
      );

      if (!response.data.consent_granted) {
        navigate('/assessment/consent', { replace: true });
        return;
      }

      setConsentChecked(true);

      const response_assessment = await jApi.request(
        'GET',
        'office365/blob_storage/account/' + selected_account
      );

      setTenantName(response_assessment.data.TenantName);
      setExecutedAt(response_assessment.data.ExecutedAt);
      setTotal(response_assessment.data.PassedCount + response_assessment.data.FailedCount);
      setPassed(response_assessment.data.PassedCount);
      setFailed(response_assessment.data.FailedCount);
      setTests(
        response_assessment.data.Tests.filter(
          (test) => test.Result === 'Failed' || test.Result === 'Passed'
        )
      );
    } catch (error) {
      setConsentChecked(true);
      // setErrorMessage(error.toString());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

const selectedIndex = useMemo(() => {
  if (!selectedRow) return -1;
  return sortedRows.findIndex((t) => t.Id === selectedRow.Id);
}, [sortedRows, selectedRow]);

const totalCount = sortedRows.length;

const goPrev = () => {
  if (selectedIndex <= 0) return;
  setSelectedRow(sortedRows[selectedIndex - 1]);
};

const goNext = () => {
  if (selectedIndex === -1 || selectedIndex >= totalCount - 1) return;
  setSelectedRow(sortedRows[selectedIndex + 1]);
};


  const refreshAssessment = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      const selected_account = sessionStorage.getItem('account');

      const response_assessment = await jApi.request(
        'GET',
        'office365/blob_storage/account/' + selected_account
      );

      setTenantName(response_assessment.data.TenantName);
      setExecutedAt(response_assessment.data.ExecutedAt);
      setTotal(response_assessment.data.PassedCount + response_assessment.data.FailedCount);
      setPassed(response_assessment.data.PassedCount);
      setFailed(response_assessment.data.FailedCount);
      setTests(
        response_assessment.data.Tests.filter(
          (test) => test.Result === 'Failed' || test.Result === 'Passed'
        )
      );
    } catch (error) {
      // setErrorMessage(error.toString() || 'Failed to refresh assessment data');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async () => {
    try {
      setErrorMessage('');
      setIsJobPosting(true);
      const selected_account = sessionStorage.getItem('account');
      const body = {
        functionpath: 'cma/assessment/Get-MaesterAudit.ps1',
        splat: {
          account: selected_account,
        },
      };
      await jApi.request('POST', 'ves/job', body);
      setSnackbarMessage(
        'Your assessment has been queued for processing. Please check back in a few minutes for the results'
      );
      setSnackbarOpen(true);
    } catch (ex) {
      // setErrorMessage(ex.toString());
    } finally {
      setIsJobPosting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High':
        return {
          bg: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
          text: theme.palette.error.main,
          border: `1.5px solid ${alpha(theme.palette.error.main, 0.4)}`
        };
      case 'Medium':
        return {
          bg: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
          text: theme.palette.warning.main,
          border: `1.5px solid ${alpha(theme.palette.warning.main, 0.4)}`
        };
      default:
        return {
          bg: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
          text: theme.palette.success.main,
          border: `1.5px solid ${alpha(theme.palette.success.main, 0.4)}`
        };
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'Error':
        return (
          <Chip
            icon={<ErrorOutlineIcon fontSize="small" />}
            label="Error"
            size="small"
            sx={{
              borderRadius: 1,
              bgcolor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
              color: theme.palette.error.main,
              '& .MuiChip-icon': { color: theme.palette.error.main },
              border: `1.5px solid ${alpha(theme.palette.error.main, 0.4)}`,
            }}
          />
        );
      case 'Failed':
        return (
          <Chip
            icon={<WarningAmberIcon fontSize="small" />}
            label="Failed"
            size="small"
            sx={{
              borderRadius: 1,
              bgcolor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
              color: theme.palette.error.main,
              '& .MuiChip-icon': { color: theme.palette.error.main },
              border: `1.5px solid ${alpha(theme.palette.error.main, 0.4)}`,
            }}
          />
        );
      case 'Skipped':
        return (
          <Chip
            icon={<SkipNextIcon fontSize="small" />}
            label="Skipped"
            size="small"
            sx={{
              borderRadius: 1,
              bgcolor: alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.15 : 0.08),
              color: theme.palette.text.secondary,
              '& .MuiChip-icon': { color: theme.palette.text.secondary },
              border: `1.5px solid ${alpha(theme.palette.text.secondary, 0.3)}`,
            }}
          />
        );
      case 'Passed':
        return (
          <Chip
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            label="Passed"
            size="small"
            sx={{
              borderRadius: 1,
              bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.2 : 0.08),
              color: theme.palette.success.main,
              '& .MuiChip-icon': { color: theme.palette.success.main },
              border: `1.5px solid ${alpha(theme.palette.success.main, 0.4)}`,
            }}
          />
        );
      case 'NotRun':
        return (
          <Chip
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            label="Not Run"
            size="small"
            sx={{
              borderRadius: 1,
              bgcolor: alpha(theme.palette.text.secondary, theme.palette.mode === 'dark' ? 0.15 : 0.08),
              color: theme.palette.text.secondary,
              '& .MuiChip-icon': { color: theme.palette.text.secondary },
              border: `1.5px solid ${alpha(theme.palette.text.secondary, 0.3)}`,
            }}
          />
        );
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
  };

  const handleCloseBlade = () => {
    setSelectedRow(null);
  };

  if (!consentChecked) {
    return null;
  }

  return (
    <Box sx={{
      minHeight: '100vh',
    }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box
            sx={{
              pt: 3,
              pb: 2,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: alpha(theme.palette.background.paper, 0.8),
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
                },
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1.5,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: alpha(theme.palette.secondary.main, 0.1),
                    },
                  }}
                >
                  <HomeRoundedIcon
                    sx={{
                      fontSize: 20,
                      color: theme.palette.secondary.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.secondary.main,
                      fontWeight: 600,
                    }}
                  >
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                /
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  px: 1,
                  py: 0.5,
                }}
              >
                Assessment Center
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          {/* Modern Header */}
          <Grid size={12}>
            <Grow in timeout={700}>
              <Box sx={headerGradient}>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{
                    p: 3,
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: `0 8px 16px ${alpha(theme.palette.common.black, 0.2)}`,
                      border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                    }}
                  >
                    <img width="100%" height="100%" src={SpeakerImage} alt="Logo" style={{ objectFit: 'cover' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 0.5,
                      }}
                    >
                      Assessment Center
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Assess the security of your Microsoft 365 environment.
                    </Typography>
                  </Box>
                </Stack>
                {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
              </Box>
            </Grow>
          </Grid>

          <Grid size={12}>
            {isLoading ? (
              <Grow in timeout={800}>
                <Paper
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    background: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.8)
                      : alpha(theme.palette.background.paper, 0.95),
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                    borderRadius: 3,
                    boxShadow: theme.palette.mode === 'dark'
                      ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                      : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                  }}
                >
                  <CircularProgress />
                </Paper>
              </Grow>
            ) : (
              <>
                <Grow in timeout={800}>
                  <Paper
                    sx={{
                      p: 3,
                      background: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.8)
                        : alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                      borderRadius: 3,
                      boxShadow: theme.palette.mode === 'dark'
                        ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                        : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                    }}
                  >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={3}
                  flexWrap="wrap"
                  gap={2}
                >
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    Microsoft 365 Security Assessment
                  </Typography>

                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={onSubmit}
                      disabled={isJobPosting}
                    >
                      {isJobPosting ? 'Starting...' : 'Begin Assessment'}
                    </Button>

                    <Tooltip title="Refresh assessment data">
                      <IconButton
                        color="primary"
                        onClick={refreshAssessment}
                        disabled={isLoading}
                        aria-label="refresh"
                      >
                        <RefreshIcon />
                      </IconButton>
                    </Tooltip>

                    {isLoading && <CircularProgress size={24} />}
                  </Stack>
                </Stack>

                <Divider sx={{ mb: 3 }} />

                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  {tenantName || 'No Assessment Found'} •{' '}
                  <time dateTime={executedAt}>{formattedLocalTime}</time>
                </Typography>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        height: '100%',
                        background: alpha(theme.palette.info.main, 0.08),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 16px ${alpha(theme.palette.info.main, 0.2)}`,
                        },
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                          Total Tests
                        </Typography>
                        <Typography variant="h2" fontWeight="700" my={1}>
                          {total}
                        </Typography>
                        <Box sx={{ mt: 1.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={100}
                            sx={{
                              height: 8,
                              borderRadius: 4,
                              backgroundColor: alpha(theme.palette.divider, 0.1),
                              '& .MuiLinearProgress-bar': {
                                background: `linear-gradient(90deg,
                                  ${theme.palette.success.main} 0%,
                                  ${theme.palette.success.main} ${passedPercent}%,
                                  ${theme.palette.error.main} ${passedPercent}%,
                                  ${theme.palette.error.main} 100%)`,
                                borderRadius: 4,
                              },
                            }}
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        height: '100%',
                        background: alpha(theme.palette.success.main, 0.08),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 16px ${alpha(theme.palette.success.main, 0.2)}`,
                        },
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={0.5}>
                          <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                            Passed
                          </Typography>
                        </Stack>
                        <Typography variant="h2" fontWeight="700" color="success.main" my={1}>
                          {passed}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={passedPercent}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: theme.palette.success.main,
                              borderRadius: 4,
                            },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        height: '100%',
                        background: alpha(theme.palette.error.main, 0.08),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 16px ${alpha(theme.palette.error.main, 0.2)}`,
                        },
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 2.5 }}>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={0.5}>
                          <ErrorIcon sx={{ color: theme.palette.error.main, fontSize: 24 }} />
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                            Failed
                          </Typography>
                        </Stack>
                        <Typography variant="h2" fontWeight="700" color="error.main" my={1}>
                          {failed}
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={failedPercent}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: theme.palette.error.main,
                              borderRadius: 4,
                            },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
                  </Paper>
                </Grow>

                <Grow in timeout={900}>
                  <Paper
                    sx={{
                      p: 3,
                      mt: 3,
                      background: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.8)
                        : alpha(theme.palette.background.paper, 0.95),
                      backdropFilter: 'blur(20px)',
                      border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.1 : 0.2)}`,
                      borderRadius: 3,
                      boxShadow: theme.palette.mode === 'dark'
                        ? `0 4px 12px ${alpha(theme.palette.common.black, 0.3)}`
                        : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
                    }}
                  >
                <Paper
                  elevation={0}
                  sx={{
                    bgcolor: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.default, 0.4)
                      : alpha(theme.palette.background.default, 0.6),
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ p: 3, pb: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      Security Controls / CIS M365 Benchmark Results
                    </Typography>
                  </Box>

                  <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                      <TableHead>
                        <TableRow sx={{
                          bgcolor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.background.paper, 0.5)
                            : alpha(theme.palette.background.paper, 0.8)
                        }}>
                          <TableCell>
                            <TableSortLabel
                              active={orderBy === 'id'}
                              direction={orderBy === 'id' ? order : 'asc'}
                              onClick={() => handleRequestSort('id')}
                            >
                              ID
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell align="center">Severity</TableCell>
                          <TableCell align="center">Status</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {sortedRows.map((row) => {
                          const severityStyle = getSeverityColor(row.Severity);

                          return (
                            <TableRow
                              key={row.Id}
                              onClick={() => handleRowClick(row)}
                              sx={{
                                cursor: 'pointer',
                                transition: 'background-color 0.15s ease',
                                '&:nth-of-type(odd)': {
                                  bgcolor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.paper, 0.3)
                                    : alpha(theme.palette.background.paper, 0.9)
                                },
                                '&:nth-of-type(even)': {
                                  bgcolor: theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.background.paper, 0.2)
                                    : alpha(theme.palette.background.paper, 0.6)
                                },
                                '&:last-child td, &:last-child th': { border: 0 },
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark'
                                    ? `${alpha(theme.palette.primary.main, 0.15)} !important`
                                    : `${alpha(theme.palette.primary.main, 0.08)} !important`,
                                },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 'medium' }}>{row.Id}</TableCell>
                              <TableCell>{row.Title}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={row.Severity}
                                  size="small"
                                  sx={{
                                    borderRadius: 1,
                                    bgcolor: severityStyle.bg,
                                    color: severityStyle.text,
                                    fontWeight: 'medium',
                                    border: severityStyle.border,
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">{getStatusChip(row.Result)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {tests.length === 0 && (
                    <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography>No security controls found in this assessment.</Typography>
                    </Box>
                  )}
                </Paper>
                  </Paper>
                </Grow>
              </>
            )}
          </Grid>
      </Grid>

      <Grid size={12} sx={{ pb: 1, pt: 1 }}>
        <Typography variant="body1" color="error">
          {errorMessage}
        </Typography>
      </Grid>

      {/* Right-side Blade */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: bladeOpen ? 0 : '-820px',
          width: { xs: '95vw', md: '820px' },
          height: '100%',
          bgcolor: 'background.paper',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.3)',
          transition: 'right 0.35s ease-in-out',
          zIndex: 1300,

          // 👇 IMPORTANT: allow horizontal overflow to be handled inside markdown area
          overflowY: 'auto',
          overflowX: 'hidden',
            '& a': {
      color:  (theme) => theme.palette.secondary.main,           // MUI primary blue - matches Maester
      textDecoration: 'none',
      fontWeight: 500,
      '&:hover': {
        textDecoration: 'underline',
      },
      '&:visited': {
        color: (theme) => theme.palette.secondary.main,         // no purple visited links
      },
    },
        }}
      >
        {bladeOpen && selectedRow && (
          
          <Box sx={{ p: 4 }}>
     <Box sx={{ mb: 3 }}>
  {/* Top row: nav on left, close on right */}
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2,
    }}
  >
    {/* Navigation */}
    {selectedIndex >= 0 && totalCount > 0 ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, userSelect: 'none' }}>
        <IconButton
          size="small"
          onClick={goPrev}
          disabled={selectedIndex <= 0}
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            borderRadius: 2,
            width: 36,
            height: 36,
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            letterSpacing: '0.4px',
            color: 'text.secondary',
            minWidth: 90,
            textAlign: 'center',
          }}
        >
           {selectedIndex + 1}/{totalCount}
        </Typography>

        <IconButton
          size="small"
          onClick={goNext}
          disabled={selectedIndex >= totalCount - 1}
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            borderRadius: 2,
            width: 36,
            height: 36,
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    ) : (
      <Box />
    )}

    {/* Close */}
    <IconButton
      onClick={handleCloseBlade}
      size="large"
      aria-label="close"
      sx={{ color: 'text.secondary' }}
    >
      <CloseIcon fontSize="large" />
    </IconButton>
  </Box>

  {/* Title row */}
  <Typography variant="h5" fontWeight="bold" sx={{ lineHeight: 1.25 }}>
    {selectedRow.Id}: {selectedRow.Title}
  </Typography>
</Box>


            <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
              
                  <Chip
                      label={selectedRow.Severity}
                      size="small"
                      sx={{
                        borderRadius: 1,
                        bgcolor: getSeverityColor(selectedRow.Severity).bg,
                        color: getSeverityColor(selectedRow.Severity).text,
                        fontWeight: 'medium',
                        border: getSeverityColor(selectedRow.Severity).border,
                      }}
                    />


              {getStatusChip(selectedRow.Result)}
            </Stack>

            {selectedRow.ResultDetail?.TestResult && (
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  mb: 4,
                  borderRadius: 2,
                  bgcolor: selectedRow.Result === 'Passed'
                    ? alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.15 : 0.08)
                    : alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                  border: `2px solid ${selectedRow.Result === 'Passed' ? theme.palette.success.main : theme.palette.error.main}`,
                }}
              >
                <Typography
                  variant="h6"
                  color={selectedRow.Result === 'Passed' ? 'success.main' : 'error.main'}
                  gutterBottom
                >
                  Test Result
                </Typography>

                {/* ✅ FIXED MARKDOWN TABLE STYLING */}
<Box
  sx={{

    '& p': { margin: '8px 0' },
    '& ul, & ol': { margin: '8px 0', paddingLeft: '22px' },
    '& li': { margin: '4px 0' },

    overflowX: 'auto',

    '& table': {
      width: '100%',
      borderCollapse: 'separate',
      borderSpacing: 0,
      tableLayout: 'auto',
      marginTop: '12px',
      fontSize: '1rem',
    },

    '& thead th': {
      textAlign: 'left',
      fontWeight: 700,
      padding: '14px 16px',
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
      color: theme.palette.text.primary,
      backgroundColor: 'transparent',
      whiteSpace: 'nowrap',
    },

    '& tbody td': {
      padding: '14px 16px',
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
      verticalAlign: 'middle',
      color: theme.palette.text.primary,
    },

    '& th, & td': {
      borderLeft: 'none !important',
      borderRight: 'none !important',
      borderTop: 'none !important',
    },

    '& tbody tr:last-of-type td': {
      borderBottom: 'none',
    },

    '& td': {
      overflowWrap: 'anywhere',
      wordBreak: 'break-word',
    },

    '& img': {
      maxHeight: '18px',
      verticalAlign: 'middle',
    },

  }}
>
  <ReactMarkdown remarkPlugins={[remarkGfm]}>
    {selectedRow.ResultDetail.TestResult}
  </ReactMarkdown>
</Box>

              </Paper>
            )}

            {selectedRow.ResultDetail?.TestDescription && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Description
                </Typography>
                 <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedRow.ResultDetail.TestDescription}
                </ReactMarkdown>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Overlay */}
      {bladeOpen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.4)',
            zIndex: 1200,
          }}
          onClick={() => setSelectedRow(null)}
        />
      )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          message={snackbarMessage}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        />
      </Container>
    </Box>
  );
}

export default Index;
